import type { Pt } from "./pathcmds";

/** Cubic Bézier as [p0, c0, c1, p1]. */
export type Bez = [Pt, Pt, Pt, Pt];

const add = (a: Pt, b: Pt): Pt => ({ x: a.x + b.x, y: a.y + b.y });
const sub = (a: Pt, b: Pt): Pt => ({ x: a.x - b.x, y: a.y - b.y });
const mul = (a: Pt, k: number): Pt => ({ x: a.x * k, y: a.y * k });
const dot = (a: Pt, b: Pt) => a.x * b.x + a.y * b.y;
const len = (a: Pt) => Math.hypot(a.x, a.y);
export const normalize = (a: Pt): Pt => {
  const l = len(a);
  return l > 1e-12 ? { x: a.x / l, y: a.y / l } : { x: 0, y: 0 };
};

export function bezPoint(b: Bez, t: number): Pt {
  const mt = 1 - t;
  const a = mt * mt * mt, c1 = 3 * mt * mt * t, c2 = 3 * mt * t * t, d = t * t * t;
  return {
    x: a * b[0].x + c1 * b[1].x + c2 * b[2].x + d * b[3].x,
    y: a * b[0].y + c1 * b[1].y + c2 * b[2].y + d * b[3].y,
  };
}

function bezD1(b: Bez, t: number): Pt {
  const mt = 1 - t;
  return add(add(mul(sub(b[1], b[0]), 3 * mt * mt), mul(sub(b[2], b[1]), 6 * mt * t)), mul(sub(b[3], b[2]), 3 * t * t));
}

function bezD2(b: Bez, t: number): Pt {
  return add(mul(add(sub(b[2], mul(b[1], 2)), b[0]), 6 * (1 - t)), mul(add(sub(b[3], mul(b[2], 2)), b[1]), 6 * t));
}

export interface FitOptions {
  /** Max allowed distance between samples and the fitted curve. */
  tolerance: number;
  /** Safety cap on recursion (each level halves the samples). */
  maxDepth?: number;
  /**
   * Optional parameter value per sample (any increasing sequence). When samples come from a
   * known curve (e.g. a warped segment sampled at uniform t), this is a far better starting
   * parameterisation than chord length and usually avoids splitting entirely.
   */
  params?: number[];
}

/**
 * Philip Schneider's least-squares cubic fitting ("An Algorithm for Automatically Fitting
 * Digitized Curves", Graphics Gems 1990), with fixed end tangents so smooth joins between
 * warped segments stay smooth.
 *
 * tan1 points from the first sample into the curve; tan2 points from the last sample back
 * into the curve. Both must be unit length (or zero, meaning "derive from samples").
 */
export function fitCubics(pts: Pt[], tan1: Pt, tan2: Pt, opts: FitOptions): Bez[] {
  return fitCubicsDetailed(pts, tan1, tan2, opts).curves;
}

/** Like fitCubics, also returning the worst sample-to-curve distance actually achieved. */
export function fitCubicsDetailed(pts: Pt[], tan1: Pt, tan2: Pt, opts: FitOptions): { curves: Bez[]; maxError: number } {
  const out: Bez[] = [];
  const err = { max: 0 };
  if (pts.length < 2) return { curves: out, maxError: 0 };
  if (len(tan1) === 0) tan1 = normalize(sub(pts[1], pts[0]));
  if (len(tan2) === 0) tan2 = normalize(sub(pts[pts.length - 2], pts[pts.length - 1]));
  fitRec(pts, opts.params ?? null, tan1, tan2, opts.tolerance, opts.maxDepth ?? 16, 0, out, err);
  return { curves: out, maxError: err.max };
}

function fitRec(pts: Pt[], params: number[] | null, tan1: Pt, tan2: Pt, tol: number, maxDepth: number, depth: number, out: Bez[], err: { max: number }) {
  const first = pts[0];
  const last = pts[pts.length - 1];
  if (pts.length === 2) {
    const d = len(sub(last, first)) / 3;
    out.push([first, add(first, mul(tan1, d)), add(last, mul(tan2, d)), last]);
    return;
  }
  const accept = (b: Bez, e: number) => {
    out.push(b);
    err.max = Math.max(err.max, e);
  };

  let u = params ? normalizeParams(params) : chordLengthParameterize(pts);
  let bez = generateBezier(pts, u, tan1, tan2);
  // Measure error geometrically: distance to the closest point on the curve (a few Newton
  // steps from the current parameter), not at the parameter itself. Measuring at the
  // parameter overstates error and causes needless splits.
  u = project(bez, pts, u);
  let [maxErr, split] = computeMaxError(pts, bez, u);
  if (maxErr <= tol) {
    accept(bez, maxErr);
    return;
  }

  // Alternate least-squares fit and reprojection; keep the best and stop when it stalls.
  let cu = u, stalls = 0;
  for (let i = 0; i < 40 && stalls < 4; i++) {
    const cb = generateBezier(pts, cu, tan1, tan2);
    cu = project(cb, pts, cu);
    const [err2, split2] = computeMaxError(pts, cb, cu);
    if (err2 < maxErr * 0.99) {
      [u, bez, maxErr, split] = [cu, cb, err2, split2];
      stalls = 0;
    } else stalls++;
    if (maxErr <= tol) {
      accept(bez, maxErr);
      return;
    }
  }

  if (depth >= maxDepth || pts.length < 4) {
    accept(bez, maxErr);
    return;
  }

  split = Math.min(Math.max(split, 1), pts.length - 2);
  const center = normalize(sub(pts[split - 1], pts[split + 1]));
  fitRec(pts.slice(0, split + 1), params?.slice(0, split + 1) ?? null, tan1, center, tol, maxDepth, depth + 1, out, err);
  fitRec(pts.slice(split), params?.slice(split) ?? null, mul(center, -1), tan2, tol, maxDepth, depth + 1, out, err);
}

function normalizeParams(p: number[]): number[] {
  const a = p[0], span = p[p.length - 1] - a || 1;
  return p.map((x) => (x - a) / span);
}

function chordLengthParameterize(pts: Pt[]): number[] {
  const u = [0];
  for (let i = 1; i < pts.length; i++) u.push(u[i - 1] + len(sub(pts[i], pts[i - 1])));
  const total = u[u.length - 1] || 1;
  return u.map((x) => x / total);
}

function generateBezier(pts: Pt[], u: number[], tan1: Pt, tan2: Pt): Bez {
  const first = pts[0];
  const last = pts[pts.length - 1];
  let c00 = 0, c01 = 0, c11 = 0, x0 = 0, x1 = 0;
  for (let i = 0; i < pts.length; i++) {
    const t = u[i], mt = 1 - t;
    const b0 = mt * mt * mt, b1 = 3 * mt * mt * t, b2 = 3 * mt * t * t, b3 = t * t * t;
    const a1 = mul(tan1, b1);
    const a2 = mul(tan2, b2);
    c00 += dot(a1, a1);
    c01 += dot(a1, a2);
    c11 += dot(a2, a2);
    const tmp = sub(pts[i], add(mul(first, b0 + b1), mul(last, b2 + b3)));
    x0 += dot(a1, tmp);
    x1 += dot(a2, tmp);
  }
  const det = c00 * c11 - c01 * c01;
  let alpha1 = 0, alpha2 = 0;
  if (Math.abs(det) > 1e-12) {
    alpha1 = (x0 * c11 - c01 * x1) / det;
    alpha2 = (c00 * x1 - x0 * c01) / det;
  }
  const segLen = len(sub(last, first));
  const eps = 1e-6 * segLen;
  // Wu/Barsky heuristic when the least-squares solution is degenerate or flips a tangent.
  if (alpha1 < eps || alpha2 < eps) {
    const d = segLen / 3;
    return [first, add(first, mul(tan1, d)), add(last, mul(tan2, d)), last];
  }
  return [first, add(first, mul(tan1, alpha1)), add(last, mul(tan2, alpha2)), last];
}

function project(bez: Bez, pts: Pt[], u: number[], steps = 3): number[] {
  for (let i = 0; i < steps; i++) u = reparameterize(bez, pts, u);
  return u;
}

function reparameterize(bez: Bez, pts: Pt[], u: number[]): number[] {
  return pts.map((p, i) => {
    const t = u[i];
    const d = sub(bezPoint(bez, t), p);
    const d1 = bezD1(bez, t);
    const d2 = bezD2(bez, t);
    const num = dot(d, d1);
    const den = dot(d1, d1) + dot(d, d2);
    if (Math.abs(den) < 1e-12) return t;
    return Math.min(1, Math.max(0, t - num / den));
  });
}

function computeMaxError(pts: Pt[], bez: Bez, u: number[]): [number, number] {
  let max = 0;
  let split = Math.floor(pts.length / 2);
  for (let i = 1; i < pts.length - 1; i++) {
    const d = len(sub(bezPoint(bez, u[i]), pts[i]));
    if (d > max) {
      max = d;
      split = i;
    }
  }
  return [max, split];
}

/** True geometric error: max distance from each sample to the nearest point on the curves. */
export function hausdorffToSamples(curves: Bez[], samples: Pt[], perCurve = 200): number {
  // Distance to the polyline through dense curve samples (point-to-segment, not point-to-point).
  const polys = curves.map((b) => Array.from({ length: perCurve + 1 }, (_, i) => bezPoint(b, i / perCurve)));
  let max = 0;
  for (const s of samples) {
    let best = Infinity;
    for (const poly of polys)
      for (let i = 0; i < poly.length - 1; i++) {
        const a = poly[i], c = poly[i + 1];
        const vx = c.x - a.x, vy = c.y - a.y;
        const l2 = vx * vx + vy * vy || 1;
        const t = Math.max(0, Math.min(1, ((s.x - a.x) * vx + (s.y - a.y) * vy) / l2));
        best = Math.min(best, (a.x + vx * t - s.x) ** 2 + (a.y + vy * t - s.y) ** 2);
      }
    max = Math.max(max, Math.sqrt(best));
  }
  return max;
}
